import React from 'react';
import { AppCard } from '@/components/atoms';
import { TreatmentReportBasicFields } from './TreatmentReportFormSection/components/TreatmentReportBasicFields';
import { TreatmentReportMassagerFields } from './TreatmentReportFormSection/components/TreatmentReportMassagerFields';
import { TreatmentReportModals } from './TreatmentReportFormSection/components/TreatmentReportModals';
import { TreatmentReportPrintOptions } from './TreatmentReportFormSection/components/TreatmentReportPrintOptions';
import { useTreatmentReportAddressSearch } from './TreatmentReportFormSection/hooks/useTreatmentReportAddressSearch';
import { useTreatmentReportFrequency } from './TreatmentReportFormSection/hooks/useTreatmentReportFrequency';
import { useTreatmentReportHospitalSelection } from './TreatmentReportFormSection/hooks/useTreatmentReportHospitalSelection';
import { useTreatmentReportLabelChecks } from './TreatmentReportFormSection/hooks/useTreatmentReportLabelChecks';
import { useTreatmentReportMassagerLocations } from './TreatmentReportFormSection/hooks/useTreatmentReportMassagerLocations';
import { useTreatmentReportSecondFlag } from './TreatmentReportFormSection/hooks/useTreatmentReportSecondFlag';
import type { TreatmentReportFormSectionProps } from './TreatmentReportFormSection/types';

export type { TreatmentReportFormMode } from './TreatmentReportFormSection/types';

const TreatmentReportFormSection: React.FC<TreatmentReportFormSectionProps> = ({
  control,
  watch,
  setValue,
  formMode,
  massagers,
  disabled = false,
  existingReports = [],
  currentReportId,
  patientId,
  initialHospital = null,
  initialMassager = null,
  initialClinicLocation = null,
  isCopying = false,
  activeConsentContent = null,
  onActiveConsentChange,
}) => {
  const { allChecked, isIndeterminate, labelChecks, toggleAll, toggleLabel } =
    useTreatmentReportLabelChecks({ setValue, watch });
  const showSecondFlag = useTreatmentReportSecondFlag({
    control,
    existingReports,
    currentReportId,
  });
  const { handleCalcFrequency, isCalcLoading, markFrequencyTouched } =
    useTreatmentReportFrequency({
      control,
      watch,
      setValue,
      patientId,
      onActiveConsentChange,
    });
  const addressSearch = useTreatmentReportAddressSearch({ watch, setValue });
  const massagerLocations = useTreatmentReportMassagerLocations({
    control,
    watch,
    setValue,
    formMode,
    massagers,
    disabled,
    patientId,
    initialMassager,
    initialClinicLocation,
  });
  const hospitalSelection = useTreatmentReportHospitalSelection({
    control,
    setValue,
    initialHospital,
  });

  return (
    <AppCard
      className={`relative bg-[#fdf5e6] shadow-[0_2px_5px_0_rgba(0,0,0,0.16),0_2px_10px_0_rgba(0,0,0,0.12)] ${isCopying ? 'border-2 border-dashed border-orange-400' : ''}`}
    >
      {isCopying && (
        <div className="absolute top-[-14px] left-4 bg-orange-400 text-white px-3 py-0.5 rounded text-sm font-bold z-10">
          コピー作成中
        </div>
      )}

      <TreatmentReportPrintOptions
        allChecked={allChecked}
        disabled={disabled}
        isIndeterminate={isIndeterminate}
        onToggleAll={toggleAll}
      />

      <div className="grid grid-cols-1 gap-6">
        <div className="grid min-w-0 grid-cols-1 items-start gap-x-6 gap-y-4 md:grid-cols-[240px_minmax(0,1fr)] xl:grid-cols-[300px_minmax(0,1fr)]">
          <TreatmentReportBasicFields
            activeConsentContent={activeConsentContent}
            control={control}
            disabled={disabled}
            doctorOptions={hospitalSelection.doctorOptions}
            handleCalcFrequency={handleCalcFrequency}
            hospitalOptions={hospitalSelection.hospitalOptions}
            isCalcLoading={isCalcLoading}
            labelChecks={labelChecks}
            markFrequencyTouched={markFrequencyTouched}
            onOpenHospitalModal={() => hospitalSelection.setIsHospitalModalOpen(true)}
            onOpenHospitalSearch={() => hospitalSelection.setIsHospitalSearchOpen(true)}
            onToggleLabel={toggleLabel}
            setValue={setValue}
            showSecondFlag={showSecondFlag}
            watchedHospitalId={hospitalSelection.watchedHospitalId}
          />

          <TreatmentReportMassagerFields
            addressRef={addressSearch.addressRef}
            clinicLocationError={massagerLocations.clinicLocationError}
            control={control}
            disabled={disabled}
            handleAddressSearch={addressSearch.handleAddressSearch}
            handleClinicLocationChange={massagerLocations.handleClinicLocationChange}
            handleFillMassagerInfo={massagerLocations.handleFillMassagerInfo}
            handleMassagerChange={massagerLocations.handleMassagerChange}
            isAddressSearching={addressSearch.isAddressSearching}
            labelChecks={labelChecks}
            locationOptions={massagerLocations.locationOptions}
            massagerOptions={massagerLocations.massagerOptions}
            onToggleLabel={toggleLabel}
            setValue={setValue}
            showClinicLocationError={massagerLocations.showClinicLocationError}
            watchedClinicLocationId={massagerLocations.watchedClinicLocationId}
            watchedMassagerId={massagerLocations.watchedMassagerId}
            watchedYm={massagerLocations.watchedYm}
          />
        </div>
      </div>

      <TreatmentReportModals
        handleAddressSearchSelect={addressSearch.handleAddressSearchSelect}
        handleHospitalDelete={hospitalSelection.handleHospitalDelete}
        handleHospitalSave={hospitalSelection.handleHospitalSave}
        handleHospitalSelect={hospitalSelection.handleHospitalSelect}
        hospital={hospitalSelection.hospital}
        isAddressSearchModalOpen={addressSearch.isAddressSearchModalOpen}
        isHospitalModalOpen={hospitalSelection.isHospitalModalOpen}
        isHospitalSearchOpen={hospitalSelection.isHospitalSearchOpen}
        setIsAddressSearchModalOpen={addressSearch.setIsAddressSearchModalOpen}
        setIsHospitalModalOpen={hospitalSelection.setIsHospitalModalOpen}
        setIsHospitalSearchOpen={hospitalSelection.setIsHospitalSearchOpen}
        watch={watch}
      />
    </AppCard>
  );
};

export default React.memo(TreatmentReportFormSection);
